import React from 'react';
import {
  Alert,
  Button,
  Card,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';

import { EllipsisText, formatDateTime } from '../Assessments/shared';
import { asOption, useModuleFilters } from './filters';
import {
  ConflictNotice,
  JobOutcomePanel,
  JobStallNotice,
  useJobRun,
} from './JobRun';
import { JsonObject, ProblemDocument, ProblemRow } from './types';

/**
 * The fleet health view: everything that is wrong right now, worst first,
 * each row saying where it is fixed.
 *
 * The ordering rule is how many people are affected and whether they can
 * see it — a trainee being graded against a broken answer key outranks an
 * operator action that will refuse, which outranks a stale snapshot, which
 * outranks hygiene. The backend orders the document that way already; the
 * screen sorts by severity anyway so a shape change degrades into a
 * slightly worse order rather than a misleading one.
 */

const SEVERITY_RANK: Record<string, number> = { error: 0, warn: 1 };

export const sortProblems = (rows: ProblemRow[]): ProblemRow[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort(
      (a, b) =>
        (SEVERITY_RANK[a.row.severity] ?? 9) -
          (SEVERITY_RANK[b.row.severity] ?? 9) || a.index - b.index,
    )
    .map(entry => entry.row);

/**
 * The row shape the server actually sends: `subjects` (a LIST, each with its
 * own `note`), a `count` that may exceed what was listed, and
 * `subjects_truncated`. `types.ts` still describes a singular `subject` that
 * no response has ever carried, which made the Subject column render an em
 * dash for every row — and the per-subject note is where the load-bearing
 * fact lives ("fail · serving · 3 sessions attached" rather than "fail").
 * Declared here, where it is read, until types.ts is corrected.
 */
export type ProblemSubject = {
  kind?: string;
  id?: number | string | null;
  label?: string;
  note?: string;
  /** The blueprint family (and the variant on it) a subject about an answer
   *  key belongs to. The document is keyed by blueprint and identity hash,
   *  and the blueprint page is where a key is derived, audited and
   *  published -- so a subject that names its family links there. */
  blueprint_id?: number | null;
  variant_id?: number | null;
  /** A job this subject alone is fixed by: one button per subject when a
   *  row is about several families. */
  job?: { kind?: string; payload?: JsonObject } | null;
};

/**
 * A remedy that is a JOB rather than a screen: the row is fixed by submitting
 * one, so the row offers it as a button. `job_kind` names the kind (the
 * answer-key derivation unless the document says otherwise) and `payload`
 * is what one submission carries when the row is about one target.
 */
export type ProblemJobFix = NonNullable<ProblemRow['fix']> & {
  job_kind?: string;
  payload?: JsonObject;
};

/** The job a remedy submits when the document does not name one. */
export const DEFAULT_REMEDY_JOB = 'answer_key.derive';

export type JobSubmission = {
  label: string;
  kind: string;
  payload: JsonObject;
};

export type ProblemRowWithSubjects = Omit<ProblemRow, 'fix'> & {
  subjects?: ProblemSubject[];
  count?: number;
  subjects_truncated?: boolean;
  severity_class?: string;
  fix?: ProblemJobFix | null;
};

const subjectsOf = (row: ProblemRow): ProblemSubject[] => {
  const listed = (row as ProblemRowWithSubjects).subjects;
  return Array.isArray(listed) ? listed : [];
};

export const subjectLabel = (subject: ProblemSubject): string => {
  if (subject.label) return String(subject.label);
  if (subject.kind && subject.id != null)
    return `${subject.kind} ${subject.id}`;
  return '—';
};

/** The screen that owns ONE subject, so a row about thirteen benchmarks links
 *  to thirteen benchmarks rather than to one page for all of them. A subject
 *  with no screen of its own answers null and the row does not pretend. */
export const subjectHref = (subject: ProblemSubject): string | null => {
  // A subject that names its blueprint family opens that page, with the
  // variant selected when it names one -- whatever kind the subject is.
  if (subject.blueprint_id != null) {
    return (
      `/dashboard/blueprints/${subject.blueprint_id}` +
      (subject.variant_id != null ? `?variant=${subject.variant_id}` : '')
    );
  }
  if (subject.id == null) return null;
  switch (subject.kind) {
    case 'scenario_draft':
      return `/dashboard/scenario-builder?draft=${subject.id}`;
    case 'job':
    case 'scenario_job':
      return `/dashboard/scenario-builder?job=${subject.id}`;
    case 'blueprint':
    case 'blueprint_version':
    case 'blueprint_variant':
      return `/dashboard/blueprints/${subject.id}`;
    case 'assessment_scenario':
    case 'scenario':
      return `/dashboard/assessments?scenario=${encodeURIComponent(
        String(subject.label || subject.id),
      )}`;
    default:
      return null;
  }
};

/**
 * A complete, sendable request for the rows whose only remedy is a terminal.
 *
 * The screen printed `enrich_briefings --scenario N --instruments <codes>`
 * with the placeholders still in it, beside 114 blocked drafts, which is a
 * template rather than a remedy. The row already knows which subjects it is
 * about, so the copy button hands over the command AND the list — a request
 * an operator can act on without asking which drafts.
 */
export const operatorRequest = (
  row: ProblemRow,
  fix: NonNullable<ProblemRow['fix']>,
): string => {
  const subjects = subjectsOf(row);
  const named = subjects
    .map(subject => subjectLabel(subject))
    .filter(label => label !== '—');
  const total = (row as ProblemRowWithSubjects).count ?? subjects.length;
  const more =
    total > named.length ? `\n(and ${total - named.length} more)` : '';
  return [
    row.title,
    fix.href || fix.label,
    named.length ? `Affected (${total}):\n- ${named.join('\n- ')}${more}` : '',
  ]
    .filter(Boolean)
    .join('\n\n');
};

/**
 * What a job remedy submits. Each subject carrying its own job gets its own
 * submission (a row about three families is three buttons); failing that,
 * the fix's own payload is one submission for the row. A remedy with neither
 * has nothing to send, and the screen says so rather than submitting an
 * empty job the server would only refuse.
 */
export const jobSubmissions = (
  row: ProblemRow,
  fix: ProblemJobFix,
): JobSubmission[] => {
  const fallbackKind = fix.job_kind || DEFAULT_REMEDY_JOB;
  const perSubject = subjectsOf(row)
    .filter(subject => subject.job?.payload)
    .map(subject => ({
      label: `${fix.label} · ${subjectLabel(subject)}`,
      kind: subject.job?.kind || fallbackKind,
      payload: subject.job?.payload as JsonObject,
    }));
  if (perSubject.length) return perSubject;
  if (fix.payload) {
    return [{ label: fix.label, kind: fallbackKind, payload: fix.payload }];
  }
  return [];
};

/**
 * One job remedy, submitted from the row and watched to its end. Deriving an
 * answer key retires the key now serving the family, so the consequence is
 * stated before the button does anything; a derivation that fails its
 * quality audit is a result, not a failure, and is said in those words.
 */
const JobRemedy: React.FC<{
  submission: JobSubmission;
  onDone: () => void;
}> = ({ submission, onDone }) => {
  const remedy = useJobRun();
  const result = (
    remedy.job?.status === 'SUCCEEDED' ? remedy.job.result : null
  ) as { status?: string } | null;

  const submit = async () => {
    const finished = await remedy.run(submission.kind, submission.payload);
    if (finished?.status === 'SUCCEEDED') onDone();
  };

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      <Popconfirm
        title="Regenerate this answer key?"
        description="The key now serving this family is retired, never deleted. The new one goes live if it passes the quality audit and stays a draft if it does not; while it is a draft, launches of this family are refused."
        okText="Regenerate"
        cancelText="Not now"
        onConfirm={() => void submit()}
        disabled={remedy.running}
      >
        <Button size="small" type="primary" loading={remedy.running}>
          {submission.label}
        </Button>
      </Popconfirm>
      <ConflictNotice job={remedy.conflict} />
      {remedy.error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={remedy.error}
        />
      )}
      <JobOutcomePanel
        job={remedy.job}
        successTitle={
          result?.status === 'draft'
            ? 'Derived again, but it failed the quality audit: the key stays a draft and launches of this family stay refused'
            : 'The answer key was derived again'
        }
        elapsedMs={remedy.elapsedMs}
      />
      <JobStallNotice stall={remedy.stall} />
    </Space>
  );
};

/** Where the row is fixed. The remedy's own href wins; failing that, a row
 *  about exactly one subject deep-links to that subject. */
export const problemHref = (row: ProblemRow): string | null => {
  if (row.fix?.href) return row.fix.href;
  const subjects = subjectsOf(row);
  return subjects.length === 1 ? subjectHref(subjects[0]) : null;
};

const SeverityTag: React.FC<{ severity: string }> = ({ severity }) => (
  <Tag color={severity === 'error' ? 'red' : 'gold'}>
    {severity === 'error' ? 'NEEDS ATTENTION' : 'WORTH WATCHING'}
  </Tag>
);

type Props = {
  report: ProblemDocument | null;
  loading?: boolean;
  error?: string;
  onRefresh: () => void;
};

const Problems: React.FC<Props> = ({ report, loading, error, onRefresh }) => {
  const [filters, setFilters] = useModuleFilters('problems', {
    severity: '',
    class: '',
  });
  const severity = asOption(filters.severity);
  const problemClass = asOption(filters.class);

  const all = sortProblems(report?.problems || []);
  const classes = Array.from(new Set(all.map(row => row.class))).sort();
  const rows = all.filter(
    row =>
      (!severity || row.severity === severity) &&
      (!problemClass || row.class === problemClass),
  );

  const columns: any[] = [
    {
      title: 'Severity',
      dataIndex: 'severity',
      fixed: 'left',
      width: 170,
      defaultSortOrder: 'ascend',
      sorter: (a: ProblemRow, b: ProblemRow) =>
        (SEVERITY_RANK[a.severity] ?? 9) - (SEVERITY_RANK[b.severity] ?? 9),
      render: (value: string) => <SeverityTag severity={value} />,
    },
    {
      title: 'Problem',
      width: 420,
      render: (_: unknown, row: ProblemRow) => (
        <Space direction="vertical" size={2}>
          <Typography.Text strong>{row.title}</Typography.Text>
          {row.detail && (
            <Typography.Text type="secondary">{row.detail}</Typography.Text>
          )}
          <EllipsisText code value={row.class} />
        </Space>
      ),
    },
    {
      title: 'What it is about',
      width: 320,
      render: (_: unknown, row: ProblemRow) => {
        const subjects = subjectsOf(row);
        const listed = (row as ProblemRowWithSubjects).count ?? subjects.length;
        if (!subjects.length) {
          return <EllipsisText value="—" />;
        }
        return (
          <Space direction="vertical" size={2}>
            {subjects.map((subject, index) => {
              const href = subjectHref(subject);
              const label = subjectLabel(subject);
              return (
                <Space key={index} size={6} wrap>
                  {href ? (
                    <Link to={href}>{label}</Link>
                  ) : (
                    <Typography.Text>{label}</Typography.Text>
                  )}
                  {subject.note && (
                    <Typography.Text type="secondary">
                      {subject.note}
                    </Typography.Text>
                  )}
                </Space>
              );
            })}
            {/* A list that silently stops is a list nobody can trust: say how
                many were left off, not merely that some were. */}
            {(row as ProblemRowWithSubjects).subjects_truncated && (
              <Typography.Text type="secondary">
                {`and ${listed - subjects.length} more of ${listed}`}
              </Typography.Text>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Since',
      dataIndex: 'since',
      width: 180,
      className: 'assessment-cell-nowrap',
      sorter: (a: ProblemRow, b: ProblemRow) =>
        String(a.since || '').localeCompare(String(b.since || '')),
      render: (value: string | null) => (value ? formatDateTime(value) : '—'),
    },
    {
      title: 'Where it is fixed',
      width: 380,
      render: (_: unknown, row: ProblemRow) => {
        const fix = row.fix;
        if (!fix || !fix.label) {
          // Said plainly, because "no automatic remedy" reads as "try
          // harder". Nobody in this product can fix these from a screen.
          return (
            <Typography.Text type="secondary">
              No admin action — engineering ticket. This row is here to be seen
              and decided on.
            </Typography.Text>
          );
        }
        if (fix.kind === 'job') {
          const submissions = jobSubmissions(row, fix as ProblemJobFix);
          return (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {submissions.length ? (
                submissions.map(submission => (
                  <JobRemedy
                    key={submission.label}
                    submission={submission}
                    onDone={onRefresh}
                  />
                ))
              ) : (
                <Typography.Text type="secondary">
                  {`${fix.label}: this remedy names no blueprint to derive from, so there is nothing to submit from here. Open the family's blueprint page and regenerate it there.`}
                </Typography.Text>
              )}
              {fix.detail && (
                <Typography.Text type="secondary">{fix.detail}</Typography.Text>
              )}
            </Space>
          );
        }
        if (fix.kind === 'cli') {
          // A template with placeholders is not a remedy an admin can use.
          // What they CAN do is send a complete request, so the copyable
          // block carries the real subjects this row is about.
          const request = operatorRequest(row, fix);
          return (
            <Space direction="vertical" size={2}>
              <Typography.Text type="secondary">
                No admin action yet — this needs an operator with a shell.
                {` ${
                  (row as ProblemRowWithSubjects).count ??
                  subjectsOf(row).length
                } affected.`}
              </Typography.Text>
              <Typography.Text code copyable={{ text: request }}>
                {fix.href || fix.label}
              </Typography.Text>
              {fix.detail && (
                <Typography.Text type="secondary">{fix.detail}</Typography.Text>
              )}
            </Space>
          );
        }
        const href = problemHref(row);
        return (
          <Space direction="vertical" size={2}>
            {href ? (
              <Link to={href}>{fix.label}</Link>
            ) : (
              <Typography.Text>{fix.label}</Typography.Text>
            )}
            {fix.detail && (
              <Typography.Text type="secondary">{fix.detail}</Typography.Text>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card
      title="Problems"
      extra={
        <Button icon={<ReloadOutlined />} loading={loading} onClick={onRefresh}>
          Refresh
        </Button>
      }
    >
      {error && (
        <Alert
          className="assessment-callout"
          type="error"
          showIcon
          title={error}
        />
      )}
      <Alert
        className="assessment-callout"
        showIcon
        type={
          report?.counts?.error
            ? 'error'
            : report?.counts?.warn
              ? 'warning'
              : 'success'
        }
        title={
          report
            ? `${report.counts.error} ${
                report.counts.error === 1 ? 'problem needs' : 'problems need'
              } attention, ${report.counts.warn} worth watching.`
            : 'The problem document has not been read yet.'
        }
        description={
          report ? (
            <Typography.Text type="secondary">
              Checked {formatDateTime(report.checked_at)}. Rows about answer
              keys are read from their stored quality-audit verdicts, so their
              age is the age of the last audit, not of this page.
            </Typography.Text>
          ) : undefined
        }
      />
      <Space className="assessment-filter-row" wrap>
        <Select
          allowClear
          aria-label="Severity"
          placeholder="Every severity"
          style={{ width: 220 }}
          value={severity}
          onChange={value => setFilters({ severity: value || '' })}
          options={[
            { value: 'error', label: 'Needs attention' },
            { value: 'warn', label: 'Worth watching' },
          ]}
        />
        <Select
          allowClear
          aria-label="Problem class"
          placeholder="Every kind of problem"
          style={{ width: 280 }}
          value={problemClass}
          onChange={value => setFilters({ class: value || '' })}
          options={classes.map(value => ({ value, label: value }))}
        />
      </Space>
      <Table
        rowKey={(row: ProblemRow, index?: number) => `${row.class}-${index}`}
        className="assessment-table"
        tableLayout="fixed"
        loading={loading}
        dataSource={rows}
        columns={columns}
        scroll={{ x: 1390 }}
        pagination={{
          defaultPageSize: 25,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100],
          showTotal: total => `${total} problem${total === 1 ? '' : 's'}`,
        }}
        locale={{
          emptyText: report
            ? 'Nothing in this document is wrong right now.'
            : 'No problem document has been loaded.',
        }}
      />
    </Card>
  );
};

export default Problems;
